import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Badge } from "./ui";

const EMPTY = { name: "", phone: "", email: "", sms: true, whatsapp: true, email_on: true, is_active: true };

const Toggle = ({ label, on, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!on)} data-testid={testid}
          className={`text-[11px] px-2.5 py-1 border transition-colors duration-150 ${
            on ? "border-brass bg-petal/30 text-ink" : "border-brass-light text-muted hover:border-brass"}`}>
    {label}
  </button>
);

export default function OpsContactsPage() {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/ops-contacts"); setRows(data.contacts); }
    catch (e) { setMsg(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true); setMsg("");
    try { await api.post("/admin/ops-contacts", draft); setDraft(EMPTY); await load(); }
    catch (e) { setMsg(errText(e)); }
    finally { setBusy(false); }
  };

  const patch = async (row, changes) => {
    setMsg("");
    try { await api.put(`/admin/ops-contacts/${row.id}`, { ...row, ...changes }); await load(); }
    catch (e) { setMsg(errText(e)); }
  };

  const remove = async (row) => {
    try { await api.delete(`/admin/ops-contacts/${row.id}`); await load(); }
    catch (e) { setMsg(errText(e)); }
  };

  if (!rows) return <p className="text-sm text-muted" data-testid="ops-contacts-loading">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="admin-ops-contacts-page">
      <div>
        <h1 className="font-display text-3xl text-ink">Alert contacts</h1>
        <p className="text-sm text-muted max-w-2xl">
          Everyone here is pinged the moment something needs a human: an order nobody accepted, a delivery at risk,
          a card that was never charged. Pick the channels each person actually reads. Admin e-mail accounts always
          get the e-mail — these contacts are on top of that.
        </p>
      </div>

      {msg ? <p className="text-danger text-sm" data-testid="ops-contacts-error">{msg}</p> : null}

      <div className="card divide-y divide-brass-light/60" data-testid="ops-contacts-list">
        {rows.length === 0 ? (
          <p className="p-4 text-sm text-muted" data-testid="ops-contacts-empty">
            No alert contacts yet. Add the first one below.
          </p>
        ) : rows.map((r) => (
          <div key={r.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`ops-contact-${r.id}`}>
            <div className="min-w-[180px]">
              <p className="text-sm text-ink">{r.name}</p>
              <p className="text-xs text-muted">{[r.phone, r.email].filter(Boolean).join(" · ") || "—"}</p>
            </div>
            <div className="flex gap-1.5">
              <Toggle label="SMS" on={r.sms} testid={`ops-contact-sms-${r.id}`} onChange={(v) => patch(r, { sms: v })} />
              <Toggle label="WhatsApp" on={r.whatsapp} testid={`ops-contact-wa-${r.id}`} onChange={(v) => patch(r, { whatsapp: v })} />
              <Toggle label="E-mail" on={r.email_on} testid={`ops-contact-email-${r.id}`} onChange={(v) => patch(r, { email_on: v })} />
            </div>
            <div className="flex-1" />
            <Badge tone={r.is_active ? "good" : "ink"} testid={`ops-contact-state-${r.id}`}>
              {r.is_active ? "on duty" : "paused"}
            </Badge>
            <button className="btn-ghost text-[11px]" data-testid={`ops-contact-toggle-${r.id}`}
                    onClick={() => patch(r, { is_active: !r.is_active })}>
              {r.is_active ? "Pause" : "Resume"}
            </button>
            <button className="text-danger" data-testid={`ops-contact-remove-${r.id}`} aria-label="Remove"
                    onClick={() => remove(r)}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      <div className="card p-4 space-y-3" data-testid="ops-contact-new">
        <p className="smallcaps">Add a contact</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <input className="field" placeholder="Name (e.g. Omar · night shift)" value={draft.name}
                 data-testid="ops-contact-name" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <input className="field" placeholder="+212 6…" value={draft.phone}
                 data-testid="ops-contact-phone" onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
          <input className="field" placeholder="name@fleurinelle.com" value={draft.email}
                 data-testid="ops-contact-email-input" onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Toggle label="SMS" on={draft.sms} testid="ops-contact-new-sms" onChange={(v) => setDraft({ ...draft, sms: v })} />
          <Toggle label="WhatsApp" on={draft.whatsapp} testid="ops-contact-new-wa" onChange={(v) => setDraft({ ...draft, whatsapp: v })} />
          <Toggle label="E-mail" on={draft.email_on} testid="ops-contact-new-email" onChange={(v) => setDraft({ ...draft, email_on: v })} />
          <div className="flex-1" />
          <button className="btn-primary text-[11px] px-4 inline-flex items-center gap-2" disabled={busy || !draft.name}
                  onClick={add} data-testid="ops-contact-add">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add contact
          </button>
        </div>
        <p className="text-[11px] text-muted">
          SMS and WhatsApp need the Twilio/Infobip and WhatsApp keys saved under Settings → Email, SMS & WhatsApp.
        </p>
      </div>
    </div>
  );
}
